import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import type { Options } from 'highcharts';
import { useTranslation } from 'react-i18next';
import HighchartsChart from '@components/charts/HighchartsChart';
import ScrollableTableContainer from '@components/common/ScrollableTableContainer';
import {
  reportingService,
  type FindingsTrend,
  type TrendBucket,
  type Severity,
} from '@services/reportingService';

const DAY_MS = 24 * 60 * 60 * 1000;
/** YYYY-MM-DD in UTC — the shape native <input type="date"> uses and the API accepts. */
const isoDay = (d: Date) => d.toISOString().slice(0, 10);
/**
 * Narrowest a date field may become before its own control chrome (the native
 * picker button plus a YYYY-MM-DD value) starts clipping. Only takes effect
 * where the container is tight enough to force shrinking.
 */
const DATE_FIELD_MIN_WIDTH = 118;

const DEFAULT_TREND_FROM = isoDay(new Date(Date.now() - 90 * DAY_MS));
const DEFAULT_TREND_TO = isoDay(new Date());

interface FindingsTrendPanelProps {
  orgId: string | null;
  /** The tracked place to chart, or undefined for the whole organization. */
  contextId: string | undefined;
  /** The severity to chart, or undefined for all. */
  severity?: Severity | undefined;
}

/**
 * The open-findings trend (REV-9), split out of OrgFindingsPage (REV-824). The
 * findings page passes its list's filters; a tracked place's page passes itself
 * (REV-141). It keeps its own date range and granularity. That state lives here,
 * so it resets when the findings page's "nothing yet" panel replaces this one
 * (REV-705).
 */
export default function FindingsTrendPanel({
  orgId,
  contextId,
  severity,
}: FindingsTrendPanelProps) {
  const { t } = useTranslation();

  // REV-9: findings open-count trend over time. The chart honors the context and
  // severity it is given plus its own date range / granularity; its load is
  // independent of and non-fatal to the page around it.
  const [trendFrom, setTrendFrom] = useState<string>(DEFAULT_TREND_FROM);
  const [trendTo, setTrendTo] = useState<string>(DEFAULT_TREND_TO);
  const [trendBucket, setTrendBucket] = useState<TrendBucket>('week');
  const [trend, setTrend] = useState<FindingsTrend | null>(null);
  const [trendError, setTrendError] = useState<string | null>(null);

  const trendParams = useMemo(
    () => ({
      contextId,
      severity,
      from: trendFrom || undefined,
      to: trendTo || undefined,
      bucket: trendBucket,
    }),
    [contextId, severity, trendFrom, trendTo, trendBucket]
  );

  // Load the trend independently of the page: a failure here shows a small
  // inline notice but must not blank out what sits beside it.
  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    void (async () => {
      try {
        const data = await reportingService.getFindingsTrend(orgId, trendParams);
        if (!cancelled) {
          setTrend(data);
          setTrendError(null);
        }
      } catch {
        if (!cancelled) {
          setTrend(null);
          setTrendError(t('findings.trend.error', 'Could not load the findings trend.'));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, trendParams, t]);

  const trendOptions: Options | null = trend
    ? {
        chart: { type: 'area', height: 300 },
        title: { text: undefined },
        xAxis: {
          categories: trend.series.map((p) => p.date),
          title: { text: t('findings.trend.axisDate', 'Date') },
        },
        yAxis: {
          title: { text: t('findings.trend.axisOpen', 'Open findings') },
          allowDecimals: false,
          min: 0,
        },
        legend: { enabled: false },
        series: [
          {
            type: 'area',
            name: t('findings.trend.openSeries', 'Open findings'),
            data: trend.series.map((p) => p.open),
          },
        ],
      }
    : null;

  // REV-9: open-count trend over time. Honors the context and severity it is
  // given plus its own date range / granularity. Chart is paired with an
  // accessible data table (WCAG dogfood).
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 1,
        }}
      >
        <Typography variant="h6" component="h2">
          {t('findings.trend.title', 'Open findings over time')}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} useFlexGap flexWrap="wrap">
          {/* REV-490: From and To are one logical range, so they stay on a
              single row. They previously sat directly in the wrapping Stack
              alongside Granularity, which let the three controls break across
              lines and strand From above To. Granularity still wraps below
              independently — it is a separate control, not part of the range. */}
          <Stack
            direction="row"
            spacing={2}
            useFlexGap
            flexWrap="nowrap"
            data-testid="trend-date-range"
            // A flex item defaults to min-width: auto and will not shrink
            // below its content; minWidth: 0 lifts that so the pair can
            // narrow where the container allows it.
            //
            // Measured honestly: down to 360px the row fits. At 320px it
            // still overflows by ~31px, because two native <input type=date>
            // controls have an intrinsic width that flex-shrink cannot get
            // under — the fields stay ~176px regardless of these values. That
            // is a known limitation, not something this fixes. 360px
            // (iPhone SE) is the practical floor; going narrower needs a
            // custom date control, not more flex tuning.
            sx={{ minWidth: 0 }}
          >
            <TextField
              id="findings-trend-from"
              label={t('findings.trend.from', 'From')}
              type="date"
              size="small"
              value={trendFrom}
              onChange={(e) => setTrendFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: trendTo || DEFAULT_TREND_TO }}
              sx={{ flex: '1 1 0', minWidth: DATE_FIELD_MIN_WIDTH }}
            />
            <TextField
              id="findings-trend-to"
              label={t('findings.trend.to', 'To')}
              type="date"
              size="small"
              value={trendTo}
              onChange={(e) => setTrendTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: trendFrom || undefined }}
              sx={{ flex: '1 1 0', minWidth: DATE_FIELD_MIN_WIDTH }}
            />
          </Stack>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="findings-trend-bucket-label">
              {t('findings.trend.granularity', 'Granularity')}
            </InputLabel>
            <Select
              labelId="findings-trend-bucket-label"
              id="findings-trend-bucket"
              value={trendBucket}
              label={t('findings.trend.granularity', 'Granularity')}
              onChange={(e) => setTrendBucket(e.target.value as TrendBucket)}
            >
              <MenuItem value="day">{t('findings.trend.day', 'Daily')}</MenuItem>
              <MenuItem value="week">{t('findings.trend.week', 'Weekly')}</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Box>

      {trendError ? (
        <Alert severity="warning" role="status">
          {trendError}
        </Alert>
      ) : !trend ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} aria-label={t('common.loading', 'Loading...')} />
        </Box>
      ) : trend.series.length === 0 ? (
        <Typography color="text.secondary">
          {t('findings.trend.empty', 'No data for the selected range.')}
        </Typography>
      ) : (
        <>
          {trendOptions && (
            <HighchartsChart
              options={trendOptions}
              description={t('findings.trend.chartDescription', {
                defaultValue:
                  'Line chart of open findings over time. Currently {{current}} open; {{opened}} opened and {{closed}} closed over the selected range.',
                current: trend.totals.current_open,
                opened: trend.totals.opened_in_range,
                closed: trend.totals.closed_in_range,
              })}
            />
          )}
          {/* Accessible data-table equivalent for the chart (WCAG dogfood). */}
          <Box component="details" sx={{ mt: 2 }}>
            <Box component="summary" sx={{ cursor: 'pointer', color: 'primary.main' }}>
              {t('findings.trend.tableToggle', 'View trend as a data table')}
            </Box>
            <ScrollableTableContainer sx={{ mt: 1 }}>
              <Table size="small" aria-label={t('findings.trend.title', 'Open findings over time')}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('findings.trend.colDate', 'Date')}</TableCell>
                    <TableCell align="right">{t('findings.trend.colOpen', 'Open')}</TableCell>
                    <TableCell align="right">{t('findings.trend.colNew', 'New')}</TableCell>
                    <TableCell align="right">{t('findings.trend.colClosed', 'Closed')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {trend.series.map((p) => (
                    <TableRow key={p.date}>
                      <TableCell component="th" scope="row">
                        {p.date}
                      </TableCell>
                      <TableCell align="right">{p.open}</TableCell>
                      <TableCell align="right">{p.new}</TableCell>
                      <TableCell align="right">{p.closed}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollableTableContainer>
          </Box>
        </>
      )}
    </Paper>
  );
}
